import HighchartsReact from 'highcharts-react-official';
import { ReactElement, forwardRef, useRef } from 'react';
import { WidgetType } from 'types/widget.types';
import { DataTableChartMenuSettings } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommonMenuSettings';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';
import { InteractiveElementWrapper } from 'widgets/sdk/InteractiveElements';

import { useResponsiveHighcharts } from '../ChartWidget.hooks';
import { ChartWidgetToolbar } from '../ChartWidgetToolbar';
import { HighChartsWrapper } from '../HighChartsWrapper';
import { ChartStyle } from './HistogramStyleSetting';
import { HistogramChartWidgetData } from './histogramChartWidget.types';

export const HistogramChartWidget = ({ getWidgetMemberComponent: getWidget }: WidgetBaseProp): ReactElement => {
  const { widthPx, heightPx } = useWidget();
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { customOnResize } = useResponsiveHighcharts({ chartRef, heightPx, widthPx });
  const { openWidgetSideMenu } = useEditor();

  return (
    <WidgetBase onResize={customOnResize} onDoubleClick={openWidgetSideMenu}>
      <WidgetSideMenuSettings>
        <DataTableChartMenuSettings
          showDualRangeConfiguration
          customChartStyle={<ChartStyle />}
          dataTableProps={{
            minCols: 1,
            minSpareCols: 0,
          }}
          disableAddColumn
          headerColumnNumber={0}
          headerRowNumber={0}
          showAxisConfiguration
          showBorderColor
          showBorderWidth
          showDataLabels
          showGridLinesConfiguration
          showLabelsConfiguration
          getWidget={getWidget}
        />
      </WidgetSideMenuSettings>
      <WidgetToolbar>
        <ChartWidgetToolbar showChartSwapper={false} />
      </WidgetToolbar>
      <HistogramChart ref={chartRef} />
    </WidgetBase>
  );
};

interface HistogramChartProps {
  waitForFonts?: boolean;
}

export const ReadOnlyHistogramChartWidget = ({ includeAltTextImg }: { includeAltTextImg?: boolean }): ReactElement => {
  const { renderType, interactions } = useWidget<HistogramChartWidgetData>();
  const waitForFonts = renderType !== ReadOnlyRenderType.ThumbnailReadOnly;
  const isPreview = renderType === ReadOnlyRenderType.StructureTreeList;

  return (
    <ReadOnlyWidgetBase disableSingleSelect>
      <InteractiveElementWrapper interactions={isPreview ? interactions : undefined}>
        <HistogramChart waitForFonts={waitForFonts} />
      </InteractiveElementWrapper>
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const HistogramChart = forwardRef<HighchartsReact.RefObject, HistogramChartProps>(
  ({ waitForFonts = true }, chartRef) => {
    return <HighChartsWrapper chartRef={chartRef} chartType={WidgetType.HistogramChart} waitForFonts={waitForFonts} />;
  },
);
HistogramChart.displayName = 'HistogramChart';
